// Layout.js
import React, { useState, useEffect, useContext } from 'react';
import NavigationBar from './NavigationBar';
import { AppBar, Toolbar, Typography, IconButton, Box, Avatar, Badge, Menu, MenuItem, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button } from '@mui/material';
import { Mail, Notifications, Logout, BugReport } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { UserContext } from '../context/UserContext';

const Layout = ({ children, pageTitle}) => {
  const { user, userInfo } = useContext(UserContext);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  const [bugPage, setBugPage] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchor);


  const getInitial = (name) => {
    if (!name) return '?';
    return name.trim()[0].toUpperCase();
  };
  useEffect(() => {
    if (userInfo?.profilePictureUrl) {
      const img = new Image();
      img.src = userInfo.profilePictureUrl;
    }
  }, [userInfo?.profilePictureUrl]);

  useEffect(() => {
    if (user) {
      const fetchPendingRequests = async () => {
        try {
          const userRef = doc(db, "UserInformation", user.uid);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();

          if (userData && userData.friendRequests) {
            const requests = userData.friendRequests;
            setPendingRequests(requests);
            console.log(requests);
          }
        } catch (error) {
          console.error("Error fetching pending requests:", error);
        }
      };

      fetchPendingRequests();
    }
  }, [user]);

  const createPopup = (messageText, type = 'success') => {
    const popup = document.createElement('div');
    popup.className = `popup ${type}`;
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'popup-close';
    closeBtn.innerHTML = '×';
    closeBtn.onclick = () => popup.remove();
    
    const message = document.createElement('div');
    message.className = 'popup-message';
    message.innerHTML = messageText;
    
    popup.appendChild(closeBtn);
    popup.appendChild(message);
    document.body.appendChild(popup);

    setTimeout(() => {
        popup.remove();
    }, 2000);
  };

  const handleBugSubmit = async () => {
    if (!bugPage || !bugDescription) {
      createPopup("Please fill all fields!", "error");
      return;
    }

    try {
      await addDoc(collection(db, 'bug_reports'), {
        userId: user.uid,
        page: bugPage,
        description: bugDescription,
        timestamp: serverTimestamp(),
      });
      
      setBugDialogOpen(false);
      setBugPage('');
      setBugDescription('');
      createPopup("Bug report submitted!", "success");
    } catch (error) {
      console.error("Error submitting bug report:", error);
      createPopup("Bug report submitted!", "error");
    }
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        console.log("User signed out successfully.");
        navigate('/login');
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  const acceptRequest = async (request) => {
    try {
      const senderRef = doc(db, 'UserInformation', request.senderId);
      const receiverRef = doc(db, 'UserInformation', user.uid);

      const receiverDoc = await getDoc(receiverRef);
      const receiverData = receiverDoc.data();
      const receiverName = receiverData ? receiverData.name : 'Unknown';
      const senderName = request.senderName;

      await updateDoc(senderRef, {
        friendsList: arrayUnion({
          id: user.uid,
          name: receiverName,
      }),
        friends: increment(1)
      });

      await updateDoc(receiverRef, {
        friendsList: arrayUnion({
          id: request.senderId,
          name: senderName, 
      }),
        friends: increment(1),
        friendRequests: arrayRemove({ senderId: request.senderId, senderName: request.senderName, timestamp: request.timestamp }),
      });

      setPendingRequests(pendingRequests.filter((req) => req.senderId !== request.senderId));
      console.log('Friend request accepted');
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const rejectRequest = async (request) => {
    try {
      const receiverRef = doc(db, 'UserInformation', user.uid);

      // Remove the friend request from receiver's friendRequests
      await updateDoc(receiverRef, {
        friendRequests: arrayRemove({ senderId: request.senderId, senderName: request.senderName, timestamp: request.timestamp }),
      });

      setPendingRequests(pendingRequests.filter((req) => req.senderId !== request.senderId));
      console.log('Friend request rejected');
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <NavigationBar />
      <Box 
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <AppBar position="fixed" color="transparent" elevation={0} sx={{
    zIndex: 10000, // stay above sidebar
    left: { xs: 0, md: '260px' }, // leave room for sidebar on desktop
    width: { xs: '100%', md: 'calc(100% - 260px)' } // full width on mobile
  }}>
          <Toolbar sx={{ justifyContent: 'flex-start' }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{ 
                  fontFamily: 'Bilgres-Regular',
                  fontSize: 30,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '109.2%',
                  color: '#272657',
                  marginTop: '20px',
                }}
              >
                {pageTitle}
              </Typography>

              <IconButton color="inherit" onClick={() => setBugDialogOpen(true)}>
                <BugReport />
              </IconButton>
              <IconButton color="inherit" onClick={handleNotificationClick}>
                <Notifications />
                {pendingRequests.length > 0 && (
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-dot': {
                        backgroundColor: 'red',
                        width: 6,
                        height: 6,
                      },
                    }}
                  />
                )}
              </IconButton>
              <IconButton 
                color="inherit"
                sx={{ ml: 1 }}
                onClick={handleLogout}
              >
                <Logout />
              </IconButton>
            </Box>
            <IconButton 
              sx={{ display: { xs: 'flex', md: 'none' } }} 
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32,
                  cursor: 'pointer',
                  backgroundColor: 'rgba(233, 80, 95, 1)',
                  textDecoration: 'none',
                }}
              >
                {userInfo?.profilePictureUrl ? (
                  <img 
                    src={userInfo.profilePictureUrl} 
                    alt="Profile" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <p>{getInitial(userInfo?.name || "A")}</p>
                )}
              </Avatar>
            </IconButton>
          </Toolbar>
        </AppBar>

        <Dialog open={bugDialogOpen} onClose={() => setBugDialogOpen(false)}>
          <DialogTitle>Report a Bug</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Page"
              fullWidth
              value={bugPage}
              onChange={(e) => setBugPage(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={bugDescription}
              onChange={(e) => setBugDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBugDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBugSubmit} variant="contained">Submit</Button>
          </DialogActions>
        </Dialog>

        {/* Dropdown menu for friend requests */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleNotificationClose}
        >
          {pendingRequests.length > 0 ? (
            pendingRequests.map((request, index) => (
              <MenuItem key={index}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>{request.senderName} sent you a friend request.</Typography>
                  <Box>
                    <button onClick={() => {acceptRequest(request)}}>Accept</button>
                    <button onClick={() => {rejectRequest(request)}}>Reject</button>
                  </Box>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem>No new requests</MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={mobileMenuAnchor}
          open={isMobileMenuOpen}
          onClose={() => setMobileMenuAnchor(null)}
        >
          <MenuItem onClick={() => {
            setBugDialogOpen(true);
            setMobileMenuAnchor(null);
          }}>
            <BugReport fontSize="small" sx={{ mr: 1 }} /> Report Bug
          </MenuItem>

          <MenuItem onClick={handleNotificationClick}>
            <Notifications fontSize="small" sx={{ mr: 1 }} /> Notifications
          </MenuItem>

          <MenuItem onClick={() => {
            handleLogout();
            setMobileMenuAnchor(null);
          }}>
            <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
          </MenuItem>
        </Menu>

        <Box 
          component="main"
          sx={{
            flexGrow: 1,
            padding: '1px',
            width: '100%',
            boxSizing: 'border-box',
            mt: '64px'
          }}
        >
          {children}
        </Box>

        <Box
          component="footer"
          sx={{
            marginTop: 'auto',
            marginLeft: { xs: 0, md: '260px' },
            color: '#707496',
            fontFamily: 'Satoshi',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '146%',
            textAlign: 'left',  // Changed from 'center' to 'left'
            borderTop: '1px solid #eaeaea',
          }}
        >
          © Copyright | All rights reserved | Joynet 2025
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
